import { initializeApp } from 'firebase/app'
import { dispatchWatchdogPush } from '@/utils/watchdogPush'
import { openBusWalkthroughFromPush } from '@/utils/busWalkthrough'
import * as Sentry from "@sentry/vue"
import axios from 'axios'
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithCredential,
  OAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  User,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth'
import { getStorage, ref as sRef, getBlob } from "firebase/storage"
import { getRemoteConfig, getValue, fetchAndActivate } from "firebase/remote-config"
import { getDatabase, ref, onValue, get, DatabaseReference, DataSnapshot, goOffline, goOnline } from "firebase/database"
import { getAnalytics } from 'firebase/analytics'
import {getMessaging, getToken, onMessage} from "firebase/messaging"
import {$vfm, vsModal, vsToast} from '@isagaco/vuesaga'
import { createToast, withProps } from 'mosha-vue-toastify'
import { Howl } from 'howler'
import successSound from '@/assets/success-sound.mp3'
import { useAuthStore } from '@/stores/auth'
import { clearTokenCache, registerToken, unregisterTokensOnLogout } from '@/utils/pushToken'
import { setPendingNavigation } from '@/utils/universal-link-navigation'

const firebaseConfig = {
  apiKey: "AIzaSyB_cKWxEKSgBGI4Lp6kdj5BGBheFBL2TNI",
  authDomain: "auth.rutasegura.co",
  databaseURL: "https://buseticas-7f9fd.firebaseio.com",
  projectId: "buseticas-7f9fd",
  storageBucket: "buseticas-7f9fd.appspot.com",
  messagingSenderId: "193142801973",
  appId: "1:193142801973:web:bb48f859394b89031a537c"
}
let apiURL = ''
if (process.env.NODE_ENV === 'production') {
  apiURL = 'https://us-central1-isaga-tracking.cloudfunctions.net/api'
} else {
  apiURL = `https://${import.meta.env.VITE_TAIL}/buseticas-7f9fd/us-central1/api`
}
export { apiURL, firebaseConfig }

const firebaseApp = initializeApp(firebaseConfig);
export const storage = getStorage(firebaseApp)
export const firebaseAuth = getAuth()
const remoteConfig = getRemoteConfig(firebaseApp)
remoteConfig.settings.minimumFetchIntervalMillis = 3600000

// Initialize notification sound
const notificationSound = new Howl({
  src: [successSound]
})


if (process.env.NODE_ENV === 'development') {
  // @ts-ignore
  // self.FIREBASE_APPCHECK_DEBUG_TOKEN = true
  // connectAuthEmulator(firebaseAuth, "http://localhost:9099")
}


const rtdbInit = getDatabase(firebaseApp)
export const rtdb = rtdbInit

getAnalytics(firebaseApp)

const onResume = () => {
  try {
    goOnline(rtdb)
  } catch (error) {
    console.error(error)
  }
}
const onActive = () => {
  try {
    goOnline(rtdb)
  } catch (error) {
    console.error(error)
  }
}
const onResign = () => {
  try {
    goOffline(rtdb)
  } catch (error) {
    console.error(error)
  }
}
const onPause = () => {
  try {
    goOffline(rtdb)
  } catch (error) {
    console.error(error)
  }
}


document.addEventListener("resume", onResume, false)
document.addEventListener("active", onActive, false)
document.addEventListener("pause", onPause, false)
document.addEventListener("resign", onResign, false)

export let firebaseTools = {
  getStorageBlob: async (path: string) => {
    try {
      return await getBlob(sRef(storage, path))
    } catch (error) {
      console.error(error)
      alert(error.code)
    }
  },
  getRemoteConfigValue: async (key: string) => {
    await fetchAndActivate(remoteConfig)
    return getValue(remoteConfig, key)
  },
  onNotificationListener: (_cb: any) => {
    const messaging = getMessaging(firebaseApp)
    return onMessage(messaging, (payload) => {
      _cb(payload)
    })
  },
  getMessagingToken: async () => {
    const messaging = getMessaging(firebaseApp)
    let token = null
    try {
      token = await getToken(messaging, { vapidKey: 'BIlquV66n_KWICeFJGtoDf-Mtp4aDyGqhTA38ZAAtKr0ewAT3zdrlmRh2x9jJ1gr0nwjsbeDE0APHsl0fw2btBc' })
      if (!token) console.info('No registration token available. Request permission to generate one.')
    } catch (error) {
      console.error(error)
    }
    return token
  },
  getCurrentUser: async () => firebaseAuth.currentUser,
  onIdTokenChanged: (_cb: any) => {
    firebaseAuth.onIdTokenChanged((user) => {
      _cb(user)
    })
  },
  getRef(dir: string) {
    return ref(rtdb, dir)
  },
  listen(ref: DatabaseReference, callback: (snapshot: DataSnapshot) => void) {
    return onValue(ref, callback)
  },
  get(ref: DatabaseReference) {
    return get(ref)
  },
  signInWithCredential: async(credential) => {
    console.info('using default signInWithCredential')
    const result = await signInWithCredential(firebaseAuth, credential)
    const user = result.user
    return {user, token: credential}
  },
  signUp: async ({email, password, name}: {email: string, password: string, name: string}) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password)
      const user = userCredential.user
      await updateProfile(user, {
        displayName: name
      })
      // TODO: CALL FUNCTION TO UPDATE USER IN HASURA
      const token = await userCredential.user.getIdToken(false)
      return { user, token }
    } catch (error) {
      console.error(error)
    }
  },
  getCurrentUserJWT: async (shouldRefresh?: boolean) => {
    const user = firebaseAuth.currentUser
    const refresh = typeof shouldRefresh === 'undefined' ? false : shouldRefresh
    if (user) {
      const token = await user.getIdToken(refresh)
      const jwtRefresh = await user.getIdTokenResult(refresh)
      return { token, jwtRefresh: jwtRefresh.expirationTime }
    }
    return { token: '', jwtRefresh: '' }
  },
  getCustomClaims: async (user: User, shouldRefresh: boolean=false) => {
    const testToken = await user.getIdTokenResult(shouldRefresh)
    if (typeof testToken.claims === 'undefined') return {}
    return testToken?.claims['https://hasura.io/jwt/claims'] as Record<string, string>
  },
  signOut: async (path='/') => {
    // Drop this device's token rows while the session can still authorize the delete,
    // so the next account on this device doesn't get our pushes.
    if (firebaseAuth.currentUser) {
      let fcmToken: string | null | undefined
      try { fcmToken = useAuthStore().fcmToken } catch (error) { fcmToken = null }
      await unregisterTokensOnLogout([fcmToken])
    } else {
      clearTokenCache()
    }
    await firebaseAuth.signOut()
    if (typeof FirebasePlugin !== 'undefined') {
      FirebasePlugin.getCurrentUser(function(user) {
        console.info(!!user)
        FirebasePlugin.signOutUser(() => {
          console.info('user signed out')
          FirebasePlugin.unregister(()=>{
            console.log("User unregister")
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem('askedNotificationPermission')
              localStorage.removeItem('askedBackgroundPermission')
            }
            let hash
            if (window.apk) {
              hash = window.os ? `/#${window.os}/${window.apkVersion}/${window.apk}` : ''
            } else {
              hash = window.os ? `/#${window.os}/${window.apkVersion}/` : ''
            }
            if(path.at(-1) === '/') path = path.slice(0, -1)
            window.location.href = window.location.origin + path + hash
          })
        }, (err:any) => {
          console.error("Failed to sign out user: " + err)
          Sentry.captureException(err)
        })
      }, (err:any) => {
        FirebasePlugin.unregister(()=>{
          console.log("User unregister")
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('askedNotificationPermission')
            localStorage.removeItem('askedBackgroundPermission')
          }
          let hash
          if (window.apk) {
            hash = window.os ? `/#${window.os}/${window.apkVersion}/${window.apk}` : ''
          } else {
            hash = window.os ? `/#${window.os}/${window.apkVersion}/` : ''
          }
          if(path.at(-1) === '/') path = path.slice(0, -1)
          window.location.href = window.location.origin + path + hash
        }, (err:any) => {
          console.error("Failed to unregister: " + err)
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('askedNotificationPermission')
            localStorage.removeItem('askedBackgroundPermission')
          }
          let hash
          if (window.apk) {
            hash = window.os ? `/#${window.os}/${window.apkVersion}/${window.apk}` : ''
          } else {
            hash = window.os ? `/#${window.os}/${window.apkVersion}/` : ''
          }
          if(path.at(-1) === '/') path = path.slice(0, -1)
          window.location.href = window.location.origin + path + hash
        })
      })
    } else {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('askedNotificationPermission')
        localStorage.removeItem('askedBackgroundPermission')
        localStorage.removeItem('store')
      }
      let hash
      if (window.apk) {
        hash = window.os ? `/#${window.os}/${window.apkVersion}/${window.apk}` : ''
      } else {
        hash = window.os ? `/#${window.os}/${window.apkVersion}/` : ''
      }
      if(path.at(-1) === '/') path = path.slice(0, -1)
      window.location.href = window.location.origin + path + hash
    }
  }
}

export const initFirebase = async () => {
  return new Promise((res,rej)=>{
    // Must inject all plugins
    let os = location.hash.slice(1, location.hash.length).split('/')[0]
    let version = location.hash.slice(1, location.hash.length).split('/')[1] || '0.0.0'
    let apk = location.hash.slice(1, location.hash.length).split('/')[2]
    
    // Preserve hash information across reloads
    if (os === 'android' || os === 'ios') {
      // Hash has valid Cordova info - store it (this updates to new version if provided)
      const hashInfo = {
        os,
        version,
        apk: apk || ''
      }
      localStorage.setItem('cordova_hash_info', JSON.stringify(hashInfo))
      console.log('💾 Stored Cordova hash info:', hashInfo)
    } else {
      // Hash doesn't have Cordova info - try to restore from localStorage
      // This only happens on reload when the hash gets lost
      const storedHashInfo = localStorage.getItem('cordova_hash_info')
      const alreadyRestored = sessionStorage.getItem('cordova_hash_restored')
      
      if (storedHashInfo) {
        try {
          const parsed = JSON.parse(storedHashInfo)
          // Always set the variables from localStorage so plugins can inject
          os = parsed.os
          version = parsed.version || '0.0.0'
          apk = parsed.apk || ''
          
          // Only update the URL hash if not already done
          if (!alreadyRestored) {
            // Get the current route from hash (e.g., "staffRoutes/" from "#/staffRoutes/")
            const currentHash = location.hash
            const routePart = currentHash.startsWith('#/') ? currentHash.substring(2) : ''
            
            // Build the complete hash with restored Cordova info + current route
            const cordovaPrefix = apk ? `${os}/${version}/${apk}` : `${os}/${version}`
            const restoredHash = routePart ? `#/${cordovaPrefix}/${routePart}` : `#/${cordovaPrefix}`
            
            // Set flag to prevent infinite loop (cleared on page reload)
            sessionStorage.setItem('cordova_hash_restored', 'true')
            
            location.hash = restoredHash
            console.log('🔄 Restored missing Cordova hash:', restoredHash)
          } else {
            console.log('ℹ️ Hash already restored in this session, using stored values:', { os, version, apk })
          }
        } catch (error) {
          console.error('❌ Error restoring hash info:', error)
        }
      }
    }
    
    if (os === 'android' || os === 'ios') {
      let system = ''
      if (typeof apk !== 'undefined') {
        system = `/${os}/${apk}/${version}/cordova.js`
      } else {
        system = `/${os}/${version}/cordova.js`
      }
      let cordovaScript = document.createElement('script')
      cordovaScript.setAttribute('src', system)
      cordovaScript.setAttribute('type', 'text/javascript')
      document.getElementsByTagName('head')[0].appendChild(cordovaScript)
      document.addEventListener('deviceready', async () => {
        window.parent.cordova = cordova
        window.os = os
        window.apkVersion = version
        window.apk = apk
        // WKWebView ships with the edge swipe-back gesture off; cordova-ios exposes the toggle
        if (os === 'ios') window.WkWebView?.allowsBackForwardNavigationGestures(true)
        // if (typeof navigator.splashscreen !== 'undefined') {
        //   console.info('hiding splashscreen')
        //   navigator.splashscreen.hide()
        // }
        // if (os === 'android' && typeof StatusBar !== 'undefined') {
        //   console.info('showing status bar')
        //   console.info('typeof StatusBar', StatusBar)
        //   // StatusBar.overlaysWebView(false)
        //   // StatusBar.styleDefault()
        //   // StatusBar.backgroundColorByHexString("#FFFFFF")
        // } else {
        //   StatusBar.overlaysWebView(true)
        // }
        const handle = (e:any) => {
          if (!e?.url) return;
          const u = new URL(e.url);
          const path = e.path || u.pathname;
          const params = e.params || Object.fromEntries(u.searchParams.entries());
          if (e.url !== location.href) setPendingNavigation(path, params)
          console.info('[UL]', { url: e.url, path, params });
        };
        
        const ul:any = (window as any).universalLinks;
        document.addEventListener('ul_login', (ev: any) => handle(ev.detail), false);
        if (ul) {
          ul.subscribe('ul_login', handle);
          ul.subscribe(null, handle);
          if (typeof ul.checkDeepLink === 'function') ul.checkDeepLink(0);
          (window as any).__ul_ready = true; // This is for native IOS universal links
        }

        firebaseTools['getNotificationsToken'] = async () => {
          const authStore = useAuthStore()
          return new Promise((resPlug, rejPlug) => {
            if(authStore.tokenListener) {
              console.info(authStore.tokenListener, 'ALREADY LISTENING')
              resPlug(true)
              return
            } else {
              FirebasePlugin.hasPermission(function(hasPermission){
                if (!hasPermission) {
                  FirebasePlugin.grantPermission(function(granted){
                    if (granted) {
                      // Setup notification listener after permission is granted
                      FirebasePlugin.onMessageReceived(function(message) {
                        console.info('message received', message)
                        // GPS watchdog (functions gpsSilence.ts, utils/watchdogPush.ts): the wake heals the stream, no banner;
                        // its silence notice is recorded as seen / opened and still shows.
                        if (dispatchWatchdogPush(message)) return
                        // Post-trip bus walkthrough (data.type 'busWalkthrough'): tapped or in the foreground, it opens.
                        if (openBusWalkthroughFromPush(message)) return
                        if(message.messageType === "notification"){
                          if (!message.tap) {
                            // Foreground notification - show toast
                            const title = message.title || message.aps?.alert?.title || ''
                            const body = message.body || message.aps?.alert?.body || ''
                            const toastText = title && body ? `${title}\n${body}` : title || body
                            if (toastText) {
                              notificationSound.play()
                              createToast(
                                withProps(vsToast, {
                                  type: 'info',
                                  text: toastText
                                }),
                                { hideProgressBar: true, timeout: 5000 }
                              )
                            }
                          } else if (message.tap === 'background') {
                            // Notification was tapped from background
                            if (message.aps?.alert?.title === 'Soporte' || message.title === 'Soporte') {
                              window.$crisp.push(['do', 'chat:show'])
                              window.$crisp.push(['do', 'chat:open'])
                            }
                          }
                          authStore.setLastPushTimestamp(Date.now())
                        }
                      }, function(error) {
                        console.error(error);
                      });

                      if (window.os === 'android') {
                        console.info('android device asking permission')
                        FirebasePlugin.setDefaultChannel({
                            id: 'fcm_default_channel',
                            name: 'Default'
                          },
                          function(){
                            authStore.tokenListener = true
                            FirebasePlugin.onTokenRefresh(async (fcmToken) => {
                              // Refresh fcm token
                              console.info('fcmToken', fcmToken)
                              authStore.fcmToken = fcmToken
                              await registerToken(fcmToken, 'fcm')
                            }, function(error) {
                                console.error(error);
                            });
                            resPlug(true)
                          },
                          function(error){
                            rejPlug(error)
                          })
                      } else {
                        console.info('getting ios fcm token')
                        authStore.tokenListener = true
                        FirebasePlugin.onApnsTokenReceived(async(apnsToken) => {
                          console.info('setting APN token')
                          FirebasePlugin.onTokenRefresh(async (fcmToken) => {
                            // Refresh fcm token
                            authStore.fcmToken = fcmToken
                            await registerToken(fcmToken, 'fcm')
                          }, function(error) {
                              console.error(error);
                          });
                          resPlug(true)
                        }, function(error) {
                            console.error(error);
                            rejPlug(error);
                        })
                      }
                    }
                  })
                } else {
                  authStore.tokenListener = true
                  FirebasePlugin.onMessageReceived(function(message) {
                    console.info('message received', message)
                    // GPS watchdog (functions gpsSilence.ts, utils/watchdogPush.ts): the wake heals the stream, no banner;
                    // its silence notice is recorded as seen / opened and still shows.
                    if (dispatchWatchdogPush(message)) return
                    // Post-trip bus walkthrough (data.type 'busWalkthrough'): tapped or in the foreground, it opens.
                    if (openBusWalkthroughFromPush(message)) return
                    if(message.messageType === "notification"){
                      if (!message.tap) {
                        // Foreground notification - show toast
                        const title = message.title || message.aps?.alert?.title || ''
                        const body = message.body || message.aps?.alert?.body || ''
                        const toastText = title && body ? `${title}\n${body}` : title || body
                        if (toastText) {
                          notificationSound.play()
                          createToast(
                            withProps(vsToast, {
                              type: 'info',
                              text: toastText
                            }),
                            { hideProgressBar: true, timeout: 5000 }
                          )
                        }
                      } else if (message.tap === 'background') {
                        // Notification was tapped from background
                        if (message.aps?.alert?.title === 'Soporte' || message.title === 'Soporte') {
                          window.$crisp.push(['do', 'chat:show'])
                          window.$crisp.push(['do', 'chat:open'])
                        }
                      }
                      authStore.setLastPushTimestamp(Date.now())
                    }
                  }, function(error) {
                      console.error(error);
                  });
                  FirebasePlugin.onTokenRefresh(async (fcmToken) => {
                    // Refresh fcm token
                    authStore.fcmToken = fcmToken
                    await registerToken(fcmToken, 'fcm')
                    resPlug(true)
                  }, function(error) {
                      console.error(error);
                      rejPlug(error);
                  });
                }
              })
            }
          })
        }
        // GET CREDENTIALS FROM PLUGIN
        firebaseTools['signInWithCredential'] = async (credential) => {
          console.info('using plugin signIn with Credential')
          return new Promise((resPlug, rejPlug) => {
            FirebasePlugin.signInWithCredential(credential, () => {
              //Get user data
              FirebasePlugin.getCurrentUser(async (userPlugin: { idToken:string }) => {
                try {
                  $vfm.show({
                      component: vsModal,
                      bind: { type: 'preloader' },
                    }, { title: 'Ingresando...'})
                  const customToken = await axios.post('authenticate', {
                    jwt: userPlugin.idToken,
                  })
                  const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
                  const user = userCredential.user
                  const token = await userCredential.user.getIdToken(false)
                  resPlug({ user, token })
                } catch (error) {
                  rejPlug(error)
                } finally {
                  $vfm.hideAll()
                  FirebasePlugin.signOutUser(() => {
                    console.info('User signed out')
                  }, rejPlug)
                }
              })
            }, rejPlug)
          })
        }
        firebaseTools['signInWith'] = async (providerType: string, loginData: {email:string, verificationCode:string}) => {
          return new Promise(async (resPlug, rejPlug) => {
            if (providerType === 'otp') {
              try {
                const customToken = await axios.post('useOTP', {
                  email: loginData.email,
                  otp: loginData.verificationCode
                })
                const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
                const user = userCredential.user
                const token = await userCredential.user.getIdToken(false)
                resPlug({ user, token })
              } catch (error) {
                rejPlug(error)
              }
            } 
            if (providerType === 'google') {
              FirebasePlugin.authenticateUserWithGoogle('193142801973-cktbfsknim407klg51do65gvl6e81aci.apps.googleusercontent.com',(credential) => {
                FirebasePlugin.signInWithCredential(credential, () => {
                  //Get user data
                  FirebasePlugin.getCurrentUser(async (userPlugin: { idToken:string }) => {
                    try {
                      $vfm.show({
                          component: vsModal,
                          bind: { type: 'preloader' },
                        }, { title: 'Ingresando...'})
                      const customToken = await axios.post('authenticate', {
                        jwt: userPlugin.idToken,
                      })
                      const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
                      const user = userCredential.user
                      const token = await userCredential.user.getIdToken(false)
                      resPlug({ user, token })
                    } catch (error) {
                      rejPlug(error)
                    } finally {
                      $vfm.hideAll()
                      FirebasePlugin.signOutUser(() => {
                        console.info('User signed out')
                      }, rejPlug)
                    }
                  })
                }, rejPlug)
              }, rejPlug)
            }
            if (providerType === 'microsoft.com') {
              FirebasePlugin.authenticateUserWithMicrosoft((credential:any)=>{
                  FirebasePlugin.signInWithCredential(credential, () => {
                    //Get user data
                    FirebasePlugin.getCurrentUser(async (userPlugin: { idToken:string }) => {
                      try {
                        $vfm.show({
                          component: vsModal,
                          bind: { type: 'preloader' },
                        }, { title: 'Ingresando...'})
                        const customToken = await axios.post('authenticate', {
                          jwt: userPlugin.idToken,
                        })
                        const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
                        const user = userCredential.user
                        const token = await userCredential.user.getIdToken(false)
                        resPlug({ user, token })
                      } catch (error) {
                        rejPlug(error)
                      } finally {
                        $vfm.hideAll()
                        FirebasePlugin.signOutUser(() => {
                          console.info('User signed out')
                        }, rejPlug)
                      }
                    })
                  }, rejPlug)
                // signInWithCredential(firebaseAuth, credential)
              }, rejPlug, 'es_CO')
            }
            if (providerType === 'apple.com') {
              FirebasePlugin.authenticateUserWithApple((credential:any)=>{
                  FirebasePlugin.signInWithCredential(credential, () => {
                    //Get user data
                    FirebasePlugin.getCurrentUser(async (userPlugin: { idToken:string }) => {
                      try {
                        $vfm.show({
                          component: vsModal,
                          bind: { type: 'preloader' },
                        }, { title: 'Ingresando...'})
                        const customToken = await axios.post('authenticate', {
                          jwt: userPlugin.idToken,
                        })
                        const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
                        const user = userCredential.user
                        const token = await userCredential.user.getIdToken(false)
                        resPlug({ user, token })
                      } catch (error) {
                        rejPlug(error)
                      } finally {
                        $vfm.hideAll()
                        FirebasePlugin.signOutUser(() => {
                          console.info('User signed out')
                        }, rejPlug)
                      }
                    })
                  }, rejPlug)
                // signInWithCredential(firebaseAuth, credential)
              }, rejPlug, 'es_CO')
            }
            if (providerType === 'facebook.com') {
              facebookConnectPlugin.login(['email','public_profile'], (userData) => {
                  console.log('UserInfo: ', userData)
                  facebookConnectPlugin.getAccessToken(async (token) =>{
                    const credential = FacebookAuthProvider.credential(token)
                    try {
                      $vfm.show({
                        component: vsModal,
                        bind: { type: 'preloader' },
                      }, { title: 'Ingresando...'})
                      const result = await signInWithCredential(firebaseAuth, credential)
                      const user = result.user
                      resPlug({user, token})
                    } catch (error) {
                      rejPlug(error)
                    } finally {
                      $vfm.hideAll()
                      FirebasePlugin.signOutUser(() => {
                        console.info('User signed out')
                      }, rejPlug)
                    }
                  })
                },
                (error) => {
                  rejPlug(error)
                }
              )
            }
          })
        }
        res(true)
      })
    } else {
      // Only inject maps //TODO: this should be done in the plugin beforemounted vsGoogleMap
      let system = `/cordova.js`
      let cordovaScript = document.createElement('script')
      cordovaScript.setAttribute('src', system)
      cordovaScript.setAttribute('type', 'text/javascript')
      document.getElementsByTagName('head')[0].appendChild(cordovaScript)
      document.addEventListener('deviceready', async () => {
        firebaseTools['signInWith'] = async (providerType: string, loginData: {email:string, verificationCode:string}) => {
          let provider = null
          if (providerType === 'google') {
            provider = new GoogleAuthProvider()
            provider.addScope('https://www.googleapis.com/auth/userinfo.email')
          }
          if (providerType === 'microsoft.com') {
            provider = new OAuthProvider('microsoft.com')
            provider.setCustomParameters({
              // Force re-consent.
              prompt: 'consent',
            })
          }
          if (providerType === 'facebook.com') {
            provider = new FacebookAuthProvider()
            provider.addScope('email')
            provider.addScope('public_profile')
            provider.setCustomParameters({
              'auth_type': 'rerequest',
              'display': 'popup',
            })
          }
          if (providerType === 'apple.com') {
            provider = new OAuthProvider('apple.com')
            provider.addScope('email')
            provider.addScope('name')
            provider.setCustomParameters({
              locale: 'es_ES'
            })
          }
          if (provider !== null) {
            try {
              firebaseAuth.useDeviceLanguage()
              const result = await signInWithPopup(firebaseAuth, provider)
              let credential
              switch (providerType) {
                case 'google':
                  credential = GoogleAuthProvider.credentialFromResult(result)
                  break
                case 'microsoft.com':
                  credential = OAuthProvider.credentialFromResult(result)
                  break
                case 'apple.com':
                  credential = OAuthProvider.credentialFromResult(result)
                  break
                case 'facebook.com':
                  credential = FacebookAuthProvider.credentialFromResult(result)
                  break
              }
              const token = credential?.accessToken
              const user = result.user
              return { user, token }
            } catch (error: any) {
              const errorCode = error.code
              const errorMessage = error.message
              // The email of the user's account used.
              const email = error.email
              // The AuthCredential type that was used.
              const credential = GoogleAuthProvider.credentialFromError(error)
              console.error(email, credential, errorCode, errorMessage)
              return { user: null, token: null }
            }
          } else if (providerType === 'otp') {
            const customToken = await axios.post('useOTP', {
              email: loginData.email,
              otp: loginData.verificationCode
            })
            const userCredential = await signInWithCustomToken(firebaseAuth, customToken.data.customToken)
            const user = userCredential.user
            const token = await userCredential.user.getIdToken(false)
            return { user, token }
          }
        }
      })
      res(true)
    }
  })
}
